import { IEvent, IEventEdit, IEventSearchParams } from '@/enitites/event'
import { api } from '@/plugins/axios'
import { QueryFunctionContext } from '@tanstack/vue-query'
import { eventKey } from '@/enitites/event/api/keys'
import {
  IEventAttendee,
  IEventFeedbackPostData,
  IEventFeedbacksResponse,
  IEventGuest,
  IEventGuestPayload,
  IEventPayoutInfo,
  IEventPreview,
} from '@/enitites/event/model/types'
import { AxiosResponse } from 'axios'

export const getEventEdit = async ({ queryKey }: QueryFunctionContext<ReturnType<typeof eventKey.edit>>) => {
  const [, , eventId] = queryKey
  const res = await api.get<IEventEdit>(`/events/${eventId}/edit`)
  return res.data
}

export const getEvent = async ({ queryKey }: QueryFunctionContext<ReturnType<typeof eventKey.details>>) => {
  const [, , eventId] = queryKey
  const res = await api.get<IEvent>(`/events/${eventId}`)
  return res.data
}

export const updateEvent = async (event: IEventEdit) => {
  const res = await api.post<IEventEdit>(`/events/${event.id}`, event)
  return res.data
}

export const deleteEvent = async (eventId: number) => {
  const res = await api.post<string>(`/events/${eventId}/delete`)
  return res.data
}

export const publishEvent = async (eventId: number) => {
  const res = await api.post<string>(`/events/${eventId}/publish`)
  return res.data
}

export const getGuestList = async ({ queryKey }: QueryFunctionContext<ReturnType<typeof eventKey.guestList>>) => {
  const [, , eventId] = queryKey
  const res = await api.get<IEventGuest[]>(`/events/${eventId}/guests`)
  return res.data
}

export const addGuestToEvent = async ({ eventId, data }: { eventId: number; data: IEventGuestPayload }) => {
  const res = await api.post<IEventGuestPayload, AxiosResponse<IEventGuest>>(`/events/${eventId}/guests`, data)
  return res.data
}

export const removeGuestFromEvent = async ({ eventId, guestId }: { eventId: number; guestId: number }) => {
  const res = await api.delete<string>(`/events/${eventId}/guests/${guestId}`)
  return res.data
}

export const getEventPayoutInfo = async ({
  queryKey,
}: QueryFunctionContext<ReturnType<typeof eventKey.payoutInfo>>) => {
  const [, , eventId] = queryKey
  const res = await api.get<IEventPayoutInfo>(`/events/${eventId}/payout`)
  return res.data
}

export const submitEventFeedback = async ({ eventId, data }: { eventId: number; data: IEventFeedbackPostData }) => {
  const res = await api.post(`/events/${eventId}/feedback`, data)
  return res.data
}

export const updateEventFeedback = async ({ eventId, data }: { eventId: number; data: IEventFeedbackPostData }) => {
  const res = await api.patch(`/events/${eventId}/feedback`, data)
  return res.data
}

export const getEventFeedbacks = async ({
  queryKey,
}: QueryFunctionContext<ReturnType<typeof eventKey.feedbackList>>) => {
  const [, , eventId] = queryKey
  const res = await api.get<IEventFeedbacksResponse>(`/events/${eventId}/feedbacks`)
  return res.data
}

export const triggerEventPayout = async (eventId: number) => {
  const res = await api.post(`/events/${eventId}/payout`)
  return res.data
}

export const getEventAttendees = async ({
  queryKey,
}: QueryFunctionContext<ReturnType<typeof eventKey.attendees>>): Promise<IEventAttendee[]> => {
  const [, , eventId] = queryKey
  const res = await api.get<IEventAttendee[]>(`/events/${eventId}/attendees`)
  return res.data
}

export const getEventsList = async ({
  queryKey,
  pageParam,
  signal,
}: QueryFunctionContext<ReturnType<typeof eventKey.eventsList>>) => {
  const [, , p] = queryKey
  const params = {
    end: (p as unknown as IEventSearchParams).end,
    k: (p as unknown as IEventSearchParams).keyword,
    o: (p as unknown as IEventSearchParams).orgIds,
    p: pageParam as number,
    pp: (p as unknown as IEventSearchParams).pageSize,
    s: (p as unknown as IEventSearchParams).sortBy,
    start: (p as unknown as IEventSearchParams).start,
    t: (p as unknown as IEventSearchParams).tab,
    u: (p as unknown as IEventSearchParams).userId,
    ulat: (p as unknown as IEventSearchParams).userLat,
    ulon: (p as unknown as IEventSearchParams).userLng,
  }
  const res = await api.get<IEventPreview[]>(`/events`, { params, signal })
  return res.data
}
